import type { Key } from '@react-types/shared';
import { useIsFetching } from '@tanstack/react-query';
import { useGetImageQuery } from '@vizit/api';
import { Tab, TabList, Tabs } from '@vizit/atoms';
import { FEATURE_FLAGS } from '@vizit/constants';
import { useFeatureFlag } from '@vizit/feature-flags';
import { EyeIcon, HeroScoreIcon, ImageSparkIcon, Star06Icon } from '@vizit/icons';
import * as React from 'react';
import { useAssetDetails } from '../../providers';
import { isGeneratedImageBySource } from '../../utils';
import { assetDetailsTabsStyles } from './styles';

enum AssetDetailsTabOptions {
  HeroScore = 'hero-score',
  ImageAnalysis = 'image-analysis',
  SparkIdeas = 'spark-ideas',
  SparkImages = 'spark-images',
}

interface AssetDetailsTabsProps {
  /**
   * The children of the component.
   */
  children?: React.ReactNode;
}

function AssetDetailsTabs(props: AssetDetailsTabsProps) {
  const { children } = props;

  const { currentTab, organizationId, portfolioId, assetId, onTabChange, originalImageData } = useAssetDetails();

  const queryKey = React.useMemo(
    () =>
      useGetImageQuery.getKey({
        organization_id: organizationId,
        portfolio_id: portfolioId,
        image_id: assetId,
      }),
    [organizationId, portfolioId, assetId],
  );
  const isOriginalImageFetching = useIsFetching({
    queryKey,
  });

  const handleTabChange = React.useCallback(
    (key: Key | null) => {
      const tab = key as AssetDetailsTabOptions;

      onTabChange(tab);
    },
    [onTabChange],
  );

  const isGeneratedImage = isGeneratedImageBySource(originalImageData);
  const isHeroImage = originalImageData?.image_type === 'hero';
  const isHeroScored = originalImageData?.hero_score != null;
  const isClipExperimentEnabled = useFeatureFlag(FEATURE_FLAGS.TMP_CLIP_EXPERIMENT, false);
  const isHeroSparkImagesEnabled = useFeatureFlag(FEATURE_FLAGS.HERO_SPARK_IMAGES, false);

  const showSparkIdeas = !isGeneratedImage && !(isHeroImage && isHeroSparkImagesEnabled);
  const showSparkImages =
    !isGeneratedImage &&
    (isHeroImage ? isHeroSparkImagesEnabled && isHeroScored : originalImageData?.omit_score !== true);

  return (
    <Tabs
      selectedKey={currentTab}
      onSelectionChange={handleTabChange}
      isDisabled={isOriginalImageFetching > 0}
      className="lg:h-full lg:w-full max-lg:w-[765px] max-md:w-[430px]"
    >
      <TabList className="max-md:w-full" aria-label="Asset details tabs">
        {isHeroImage && (
          <Tab id="hero-score" className={assetDetailsTabsStyles.tab} data-testid="image-details-hero-score">
            <HeroScoreIcon size={20} />
            <span>Hero score</span>
          </Tab>
        )}
        <Tab id="image-analysis" className={assetDetailsTabsStyles.tab} data-testid="image-details-analysis">
          <EyeIcon size={20} />
          <span>Analysis</span>
        </Tab>
        {showSparkIdeas && (
          <Tab
            id="spark-ideas"
            className={assetDetailsTabsStyles.tab}
            data-testid="image-details-spark-ideas"
            isDisabled={isClipExperimentEnabled}
          >
            <Star06Icon size={20} />
            <span>Spark ideas</span>
          </Tab>
        )}
        {showSparkImages && (
          <Tab
            id="spark-images"
            className={assetDetailsTabsStyles.tab}
            data-testid="image-details-spark-images"
            isDisabled={isClipExperimentEnabled}
          >
            <ImageSparkIcon size={20} isActive={currentTab === AssetDetailsTabOptions.SparkImages} />
            <span>Spark images</span>
          </Tab>
        )}
      </TabList>
      {children}
    </Tabs>
  );
}

export { AssetDetailsTabs, AssetDetailsTabOptions };
export type { AssetDetailsTabsProps };
